import React from 'react';
import { ActivityIndicator, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { LOCATION_SUMMARY_COLUMNS } from '../constants/locationSummaryColumns';
import { useLocationSummaryTable } from '../hooks/useLocationSummaryTable';
import LocationSummaryTableRow from './LocationSummaryTableRow';
import { locationSummaryTableStyles as styles } from '../styles/LocationSummaryTable.styles';
import TableSkeleton from './TableSkeleton';
import { LocationDetailsParams } from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';

interface LocationSummaryTableProps {
  filterParams?: LocationDetailsParams;
}

const LocationSummaryTable: React.FC<LocationSummaryTableProps> = ({ filterParams = {} }) => {
  const {
    rows,
    sortKey,
    sortDirection,
    onSort,
    toggleExpanded,
    loading,
    currentPage,
    totalPages,
    total,
    pageSize,
    goToPage,
  } = useLocationSummaryTable(filterParams);

  const startRow = total === 0 ? 0 : currentPage * pageSize + 1;
  const endRow = Math.min((currentPage + 1) * pageSize, total);

  return (
    <View style={styles.container}>
      <ScrollView horizontal style={styles.tableScroll} contentContainerStyle={styles.tableContent}>
        <View style={styles.tableInner}>
          <View style={styles.headerRow}>
            {LOCATION_SUMMARY_COLUMNS.map((column) => {
              const isSorted = sortKey === column.key;
              return (
                <TouchableOpacity
                  key={column.key}
                  style={[styles.headerCell, { minWidth: column.minWidth, flex: 1 }]}
                  onPress={() => onSort(column.key)}
                  activeOpacity={0.7}
                  testID={`location-summary-sort-${column.key}`}
                >
                  <Text style={styles.headerText}>{column.label}</Text>
                  <MaterialIcons
                    name={
                      isSorted
                        ? sortDirection === 'asc'
                          ? 'keyboard-arrow-up'
                          : 'keyboard-arrow-down'
                        : 'unfold-more'
                    }
                    size={16}
                    color="#616161"
                  />
                </TouchableOpacity>
              );
            })}
          </View>

          {loading ? (
            <TableSkeleton rows={10} />
          ) : rows.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No rows available.</Text>
            </View>
          ) : (
            rows.map((row, index) => (
              <LocationSummaryTableRow
                key={row.rowKey}
                row={row}
                index={index}
                onToggleExpand={toggleExpanded}
              />
            ))
          )}
        </View>
      </ScrollView>

      {/* Pagination bar */}
      {totalPages > 1 && (
        <View style={paginationStyles.bar}>
          <Text style={paginationStyles.info}>
            {`${startRow}–${endRow} of ${total}`}
          </Text>
          <View style={paginationStyles.controls}>
            <TouchableOpacity
              onPress={() => goToPage(0)}
              disabled={currentPage === 0}
              style={[paginationStyles.btn, currentPage === 0 && paginationStyles.btnDisabled]}
              testID="pagination-first"
            >
              <MaterialIcons name="first-page" size={20} color={currentPage === 0 ? '#D1D5DB' : '#374151'} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              style={[paginationStyles.btn, currentPage === 0 && paginationStyles.btnDisabled]}
              testID="pagination-prev"
            >
              <MaterialIcons name="chevron-left" size={20} color={currentPage === 0 ? '#D1D5DB' : '#374151'} />
            </TouchableOpacity>
            <Text style={paginationStyles.pageLabel}>{`Page ${currentPage + 1} of ${totalPages}`}</Text>
            <TouchableOpacity
              onPress={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              style={[paginationStyles.btn, currentPage >= totalPages - 1 && paginationStyles.btnDisabled]}
              testID="pagination-next"
            >
              <MaterialIcons name="chevron-right" size={20} color={currentPage >= totalPages - 1 ? '#D1D5DB' : '#374151'} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => goToPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              style={[paginationStyles.btn, currentPage >= totalPages - 1 && paginationStyles.btnDisabled]}
              testID="pagination-last"
            >
              <MaterialIcons name="last-page" size={20} color={currentPage >= totalPages - 1 ? '#D1D5DB' : '#374151'} />
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

import { StyleSheet } from 'react-native';
import { FontFamily } from '../../../constants/fonts';

const paginationStyles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#D7E5F8',
    backgroundColor: '#FAFCFF',
  },
  info: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: FontFamily.regular,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  btn: {
    padding: 4,
    borderRadius: 4,
  },
  btnDisabled: {
    opacity: 0.4,
  },
  pageLabel: {
    fontSize: 12,
    color: '#374151',
    fontFamily: FontFamily.medium,
    marginHorizontal: 8,
  },
});

export default LocationSummaryTable;

